import classNames from 'classnames';
import type { FlexProps as FlexProperty, FullWidthProps, PolymorphicComponentProps } from '../types.ts';
import * as styles from './index.css.ts';

// Inspired by Radix UI's Flex component:
// https://www.radix-ui.com/themes/docs/components/flex

type FlexProps<T extends React.ElementType> = FullWidthProps &
    FlexProperty & {
        as?: T;
        display?: styles.Display;
        direction?: styles.Direction;
        align?: styles.Align;
        justify?: styles.Justify;
        wrap?: styles.Wrap;
        gap?: styles.Spacing;
        gapX?: styles.Spacing;
        gapY?: styles.Spacing;
    };

export function Flex<T extends React.ElementType>({
    as,
    display,
    direction,
    gap,
    gapX,
    gapY,
    fullWidth = false,
    align,
    justify,
    wrap,
    flex,
    className,
    ...props
}: PolymorphicComponentProps<T, FlexProps<T>>) {
    const Component = as || 'div';
    return (
        <Component
            className={classNames(
                styles.displayVariants[display ?? 'flex'],
                direction && styles.directionVariants[direction],
                align && styles.alignVariants[align],
                justify && styles.justifyVariants[justify],
                wrap && styles.wrapVariants[wrap],
                gap && styles.gapVariants[gap],
                gapX && styles.columnGapVariants[gapX],
                gapY && styles.rowGapVariants[gapY],
                flex && styles.flexVariants[flex === true ? '1' : flex],
                { [styles.fullWidth]: fullWidth },
                className
            )}
            {...props}
        />
    );
}
